import React from 'react';
import { createUseStyles } from 'react-jss';
import theme from '../../constants/Theme';
import { RecentDevice } from '../../types/diaryTypes';
import DiarySummaryBadge from '../DiarySummaryBadge/DiarySummaryBadge';
import TryOutDevices from '../TryOutDevices/TryOutDevices';
import { Headline } from '../../elements';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { formatDateDayMonthYear } from '../../helpers/dateHelpers';

type DiarySummaryWidgetVariant = 'positive' | 'neutral';

interface DeviceSummaryWidgetProps {
  device?: RecentDevice;
  variant: DiarySummaryWidgetVariant;
  isLoading?: boolean;
}

const DeviceSummaryWidget: React.FC<DeviceSummaryWidgetProps> = ({ variant, device, isLoading = false }) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      {isLoading && (
        <div className={classes.loadingContainer}>
          <LoadingOverlay isLoading={isLoading} />
          <Headline fontSize={20} marginBottom={30}>
            Hörgerät
          </Headline>
        </div>
      )}
      {device && (
        <>
          <DiarySummaryBadge variant={variant}>{`${device.positivityPercentage}% positive Einträge`}</DiarySummaryBadge>
          <TryOutDevices tryOutDeviceList={device.tryOutDeviceList} size="large" />
          <div className={classes.periodSection}>
            <span className={classes.periodLabel}>
              {device.periodBegin === device.periodEnd
                ? formatDateDayMonthYear(device.periodBegin)
                : `Zeitraum: ${formatDateDayMonthYear(device.periodBegin)} - ${formatDateDayMonthYear(device.periodEnd)}`}
            </span>
            <span className={classes.numberOfEntriesLabel}>
              {`${device.numberOfDiaryEntries} ${device.numberOfDiaryEntries === 1 ? 'Eintrag' : 'Einträge'}`}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.contrastWhite,
    padding: '12px 15px 12px 15px',
    borderRadius: theme.borderRadius.card,
    boxShadow: theme.shadows.card,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 15,
    position: 'relative',
    boxSizing: 'border-box',
    minHeight: 167,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  periodSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  periodLabel: {
    fontSize: 14,
    lineHeight: '18px',
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.blue,
  },
  numberOfEntriesLabel: {
    fontSize: 14,
    lineHeight: '18px',
    color: theme.colors.greyMain,
  },
});

export default DeviceSummaryWidget;
